import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiError, apiFetch } from '@/lib/api/client';
import { useToast } from '@/components/ui/toast';
import { type ApiInvestmentResponse, parseDecimalInput } from './types';
import { INVESTMENTS_QUERY_KEY } from './useInvestments';

/** Formulário de desinvestimento (métricas realizadas + comentário obrigatórios). */
export interface DivestForm {
  /** MOIC realizado digitado (ex.: "2,5"). */
  moicRealized: string;
  /** TIR realizada digitada em % (ex.: "18"; pode ser negativa). */
  irrRealized: string;
  comment: string;
}

export interface DivestBody {
  moicRealized: number | null;
  irrRealized: number | null;
  comment: string;
}

/** True se MOIC e TIR foram informados e há comentário — habilita o confirmar. */
export function isDivestValid(form: DivestForm): boolean {
  const moic = parseDecimalInput(form.moicRealized);
  const irr = parseDecimalInput(form.irrRealized);
  return moic != null && moic >= 0 && irr != null && form.comment.trim().length > 0;
}

/**
 * Monta o corpo do POST /divest (função pura). MOIC realizado como múltiplo; TIR digitada em % →
 * fração (18 → 0.18; aceita negativa). Assume validação prévia (isDivestValid).
 */
export function buildDivestBody(form: DivestForm): DivestBody {
  const irrPct = parseDecimalInput(form.irrRealized);
  return {
    moicRealized: parseDecimalInput(form.moicRealized),
    irrRealized: irrPct == null ? null : irrPct / 100,
    comment: form.comment.trim(),
  };
}

/**
 * Desinveste: POST /api/investments/{id}/divest com If-Match. Grava métricas realizadas + data de
 * saída + comentário (histórico). Em sucesso, invalida ficha e lista. 412 → aviso + refetch.
 */
export function useDivestInvestment() {
  const qc = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, etag, body }: { id: string; etag: string; body: DivestBody }) =>
      apiFetch<ApiInvestmentResponse>(`/api/investments/${id}/divest`, {
        method: 'POST',
        headers: { 'If-Match': `"${etag}"` },
        body,
      }),
    onSuccess: (resp) => {
      void qc.invalidateQueries({ queryKey: ['investment', resp.id] });
      void qc.invalidateQueries({ queryKey: INVESTMENTS_QUERY_KEY });
      toast(`Desinvestimento registrado: ${resp.codename}.`);
    },
    onError: (err, vars) => {
      if ((err as ApiError).status === 412) {
        toast('Esse investimento foi alterado por outra pessoa. Atualizei a ficha.', 'warn');
        void qc.invalidateQueries({ queryKey: ['investment', vars.id] });
      } else {
        toast('Não foi possível registrar o desinvestimento. Confira os campos e tente de novo.', 'danger');
      }
    },
  });
}
